import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, ChevronLeft, Gavel, Info, Loader2, Search } from "lucide-react";
import { API, JURISDICTIONS } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const JudgeAnalyticsPage = () => {
  const [form, setForm] = useState({ judge_name: "", jurisdiction: "federal", court: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const analyze = async () => {
    if (!form.judge_name.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/judges/analyze`, form);
      setResult(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  const getTendencyBadge = (value) => {
    const strValue = String(value || '').toLowerCase();
    const colors = {
      strict: "bg-red-100 text-red-700",
      high: "bg-red-100 text-red-700",
      firm: "bg-amber-100 text-amber-700",
      moderate: "bg-amber-100 text-amber-700",
      balanced: "bg-blue-100 text-blue-700",
      selective: "bg-blue-100 text-blue-700",
      lenient: "bg-emerald-100 text-emerald-700",
      low: "bg-emerald-100 text-emerald-700",
      frequent: "bg-violet-100 text-violet-700",
      rare: "bg-slate-100 text-slate-700",
      true: "bg-emerald-100 text-emerald-700",
      false: "bg-slate-100 text-slate-700",
      limited: "bg-slate-100 text-slate-700",
      broad: "bg-emerald-100 text-emerald-700",
      focused: "bg-blue-100 text-blue-700"
    };
    return colors[strValue] || "bg-slate-100 text-slate-700";
  };
  
  const isLiveData = result?.status === "success" && result?.data_source?.includes("CourtListener");
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="judge-analytics-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-5xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <div className="flex items-center gap-2 mb-2">
            <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Judge Historical Analysis</h1>
            <Badge className="bg-indigo-500">LIVE DATA</Badge>
          </div>
          <p className="text-muted-foreground">Procedural pattern analysis from public court records</p>
        </div>
        
        <Alert className="mb-6 bg-slate-100 border-slate-200">
          <Info className="h-4 w-4" />
          <AlertDescription>
            This tool analyzes historical procedural patterns from public court records and published opinions. 
            It provides factual statistics only and does not assess character, competence, or ethics.
          </AlertDescription>
        </Alert>
        
        <div className="grid gap-6 lg:grid-cols-3">
          <Card className="lg:col-span-1">
            <CardHeader><CardTitle>Search Judge</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label>Judge Name (Last Name)</Label>
                <Input 
                  value={form.judge_name} 
                  onChange={e => setForm(p => ({ ...p, judge_name: e.target.value }))}
                  placeholder="e.g., Posner, Kozinski, Wood"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Jurisdiction</Label>
                <Select value={form.jurisdiction} onValueChange={v => setForm(p => ({ ...p, jurisdiction: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {JURISDICTIONS.map(j => <SelectItem key={j.value} value={j.value}>{j.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Court (Optional)</Label>
                <Input 
                  value={form.court} 
                  onChange={e => setForm(p => ({ ...p, court: e.target.value }))}
                  placeholder="e.g., ca7, ca9"
                />
              </div>
              <Button className="w-full" onClick={analyze} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Gavel className="h-4 w-4 mr-2" />}
                Analyze Patterns
              </Button>
            </CardContent>
          </Card>
          
          {result && result.status === "success" && (
            <div className="lg:col-span-2 space-y-4 animate-fade-in">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>{result.judge?.name || form.judge_name}</CardTitle>
                      <CardDescription>
                        {isLiveData && <Badge className="bg-green-500 mr-2">Live Data</Badge>}
                        Source: {result.data_source}
                      </CardDescription>
                    </div>
                    {result.procedural_indicators?.data_confidence && (
                      <Badge variant="outline">
                        Confidence: {result.procedural_indicators.data_confidence}%
                      </Badge>
                    )}
                  </div>
                </CardHeader>
              </Card>
              
              {result.analytics && (
                <>
                  <Card>
                    <CardHeader><CardTitle>Opinion Analytics</CardTitle></CardHeader>
                    <CardContent>
                      <div className="grid gap-4 md:grid-cols-3">
                        <div className="p-4 bg-slate-50 rounded-xl text-center">
                          <p className="text-sm text-muted-foreground">Total Opinions</p>
                          <p className="text-3xl font-bold text-primary">{result.analytics.total_opinions_found}</p>
                        </div>
                        <div className="p-4 bg-slate-50 rounded-xl text-center">
                          <p className="text-sm text-muted-foreground">Opinion Volume</p>
                          <Badge className={getTendencyBadge(result.procedural_indicators?.opinion_volume)}>
                            {result.procedural_indicators?.opinion_volume || 'Unknown'}
                          </Badge>
                        </div>
                        <div className="p-4 bg-slate-50 rounded-xl text-center">
                          <p className="text-sm text-muted-foreground">Case Diversity</p>
                          <Badge className={getTendencyBadge(result.procedural_indicators?.case_diversity)}>
                            {result.procedural_indicators?.case_diversity || 'Unknown'}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {result.analytics.case_type_distribution && Object.keys(result.analytics.case_type_distribution).length > 0 && (
                    <Card>
                      <CardHeader><CardTitle>Case Type Distribution</CardTitle></CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {Object.entries(result.analytics.case_type_distribution).map(([type, count]) => (
                            <div key={type} className="flex items-center gap-3">
                              <span className="text-sm capitalize w-24">{type}</span>
                              <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-primary rounded-full" 
                                  style={{ width: `${Math.min((count / result.analytics.total_opinions_found) * 100, 100)}%` }}
                                />
                              </div>
                              <span className="text-sm font-medium w-8 text-right">{count}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  
                  {result.analytics.recent_rulings && result.analytics.recent_rulings.length > 0 && (
                    <Card>
                      <CardHeader><CardTitle>Recent Rulings</CardTitle></CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {result.analytics.recent_rulings.map((ruling, i) => (
                            <div key={i} className="p-3 bg-slate-50 rounded-lg">
                              <div className="flex justify-between items-start mb-1">
                                <h4 className="font-medium text-sm">{ruling.case_name}</h4>
                                <Badge variant="outline" className="text-xs">{ruling.date}</Badge>
                              </div>
                              <p className="text-xs text-muted-foreground">{ruling.court}</p>
                              {ruling.snippet && (
                                <p className="text-xs mt-2 line-clamp-2">{ruling.snippet}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
              
              {/* Fall back to pattern display if no live analytics */}
              {result.motion_disposition_patterns && (
                <Card>
                  <CardHeader><CardTitle>Motion Disposition Patterns</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-3">
                      {Object.entries(result.motion_disposition_patterns).map(([motion, data]) => (
                        <div key={motion} className="p-4 bg-slate-50 rounded-xl">
                          <h4 className="font-medium capitalize mb-2">{motion.replace(/_/g, ' ')}</h4>
                          <div className="space-y-2 text-sm">
                            {Object.entries(data).map(([key, value]) => (
                              <div key={key} className="flex justify-between">
                                <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                                <Badge className={getTendencyBadge(value)}>{String(value)}</Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          
          {result && result.status === "not_found" && (
            <Card className="lg:col-span-2">
              <CardContent className="p-8 text-center">
                <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Records Found</h3>
                <p className="text-muted-foreground mb-4">{result.message}</p>
                <p className="text-sm text-muted-foreground">{result.suggestions}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      <Footer />
    </main>
  );
};


export default JudgeAnalyticsPage;
